<template>
  <div
    :style="wrapperStyle"
    class="static-wallpaper-uploader static-upload-wrap"
  >
    <div
      v-if="previewUrl"
      class="upload-static-succ"
    >
      <div
        :style="`background-image:url(${previewUrl});`"
        class="pic"
      />
      <span
        class="btn-remove"
        @click="handleRemoveStaticResource"
      >
        <i class="el-icon-delete" />
      </span>
    </div>
    <div
      v-else-if="uploadStaticStatus === 'process'"
      class="upload-static-process"
    >
      <Loading
        :rotate="uploadComplete"
        class="loading-progress"
      />
      <template v-if="uploadComplete < 100">
        <p class="upload-process-text">
          上传中({{ uploadComplete }}%)
        </p>
        <p
          v-if="uploadSpeed"
          class="upload-speed"
        >
          上传速率 {{ uploadSpeed }}
        </p>
      </template>
      <p
        v-else
        class="upload-process-text"
      >
        正在处理中...
      </p>
    </div>
    <el-upload
      v-else
      :http-request="handleStaticUpload"
      accept=".jpg,.jpeg"
      action
      class="upload-static"
      drag
    >
      <i :class="`icon-${platform}`" />
      <p class="upload-info">
        将{{ platform === "pc" ? "电脑" : "手机" }}静态壁纸拖拽到这里
      </p>
      <!-- <p class="upload-sub">
        或
        <span class="btn">浏览</span>以选择文件
      </p> -->
      <p v-if="platform === 'mobile'" class="upload-tip for-mobile">
        图片尺寸不小于{{ requiredWidth }}x{{ requiredHeight }}<br>支持JPG格式<br>图片不超过20M
      </p>
      <p v-else class="upload-tip">
        图片尺寸不小于{{ requiredWidth }}x{{ requiredHeight }}，支持JPG格式，图片不超过20M
      </p>
    </el-upload>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import * as SparkMD5 from 'spark-md5'
import { isImageFileMatchRequiredSize } from '../../utils/file'
import {
  doCheckIsExists,
  doCheckUploadLimit,
  doUpload,
} from '@/api/upload'
import { parseSpeed } from '@/utils/shared'
import Loading from '@/components/Loading'

/** 限制上传文件最大 size 20M */
const FILE_MAX_SIZE = 20 * 1024 * 1024

export default {
  name: 'StaticWallpaperUploader',
  components: {
    Loading,
  },
  props: {
    resourceId: {
      type: Number,
      default: 0,
    },
    paperUrl: {
      type: String,
      default: '',
    },
    previewJpg: {
      type: String,
      default: '',
    },
    platform: {
      type: String,
      default: 'pc',
    },
    width: {
      type: String,
      default: '',
    },
    height: {
      type: String,
      default: '',
    },
    requiredWidth: {
      type: Number,
      default: 0,
    },
    requiredHeight: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      uploadStaticStatus: 'wait',
      uploadComplete: 0, // 上传进度
      uploadSpeed: '',
      uploadPreLoaded: 0,
      uploadLoaded: 0,
    }
  },
  computed: {
    ...mapState({
      userInfo: state => state.userInfo,
      common: (state) => {
        return {
          token: state.userInfo.token,
          open_id: state.userInfo.open_id,
        }
      },
    }),
    wrapperStyle() {
      return {
        width: this.width,
        height: this.height,
      }
    },
    previewUrl() {
      if (this.previewJpg && this.previewJpg.includes('jpg')) {
        return this.previewJpg
      }
      return this.paperUrl
    },
  },
  methods: {
    async checkMd5(md5, file) {
      try {
        const res = await this.doCheckIsExists(md5)
        const is_exist
          = res.is_exist !== undefined ? res.is_exist : res.data.is_exist
        if (is_exist) {
          if (res.data && res.data.resource_id) {
            this.$emit('update', res.data)

            this.uploadStaticStatus = 'succ'
          } else {
            this.$emit('error', '已经上传过该视频，无需重复上传')
          }
          return
        }

        await this.doUpload(file)
      } catch (err) {
        // TODO
      }
    },
    handleRemoveStaticResource() {
      // 清空数据
      this.$emit('update', {
        resource_id: 0,
        resource_url: '',
        preview_jpg: '',
      })
      this.uploadStaticStatus = 'wait'
    },
    getErrorMsg(err) {
      if (!err) return ''
      if (err.msg) return err.msg
      if (err.resp_common && err.resp_common.msg) return err.resp_common.msg
    },

    async handleStaticUpload($event) {
      const blobSlice = File.prototype.slice
      const file = $event.file
      try {
        await doCheckUploadLimit(
          {
            common: this.common,
            wtype: this.platform === 'mobile' ? 4 : 2,
          },
          this.platform,
        )
      } catch (err) {
        const confirmDialogText
          = this.getErrorMsg(err) || '您今日上传壁纸已超过限制，请明天再来哦~'
        this.$toast(confirmDialogText)
        console.log(confirmDialogText)
        return
      }
      try {
        await isImageFileMatchRequiredSize(file, this.requiredWidth, this.requiredHeight)
      } catch (err) {
        this.$emit('error', err.message)
        return
      }
      if (file.size > FILE_MAX_SIZE) {
        this.$emit('error', '上传的图片不能超过 20M')
        return
      }
      const chunkSize = 2097152
      const chunks = Math.ceil(file.size / chunkSize)
      let currentChunk = 0
      const spark = new SparkMD5.ArrayBuffer()
      const fileReader = new FileReader()

      fileReader.onload = (e) => {
        // console.log('read chunk nr', currentChunk + 1, 'of', chunks)
        spark.append(e.target.result)
        currentChunk++
        if (currentChunk < chunks) {
          loadNext()
        } else {
          // console.log('finished loading', spark.end())
          this.checkMd5(spark.end(), file)
        }
      }

      function loadNext() {
        const start = currentChunk * chunkSize
        const end
          = start + chunkSize >= file.size ? file.size : start + chunkSize
        fileReader.readAsArrayBuffer(blobSlice.call(file, start, end))
      }

      loadNext()
    },
    async doCheckIsExists(md5) {
      try {
        return await doCheckIsExists({
          common: this.common,
          md5,
          wtype: this.platform === 'mobile' ? 4 : 2,
        })
      } catch (err) {
        // TODO
      }
    },
    /*
     * @param type {String} static:静态壁纸，live:动态壁纸文件，preview_jpg:预览图
     */
    async doUpload(file, type = 'static') {
      const data = new FormData()
      data.append('open_id', this.common.open_id)
      data.append('token', this.common.token)
      data.append('file', file)
      data.append('file_type', type)

      this.uploadStaticStatus = 'process'

      try {
        // 计算进度
        this.uploadComplete = 0

        // 计算速率
        this.uploadSpeed = ''
        this.uploadPreLoaded = 0
        this.uploadLoaded = 0
        this.speedTimer = setInterval(() => {
          const speed = (this.uploadLoaded - this.uploadPreLoaded) / 1024
          if (speed) {
            this.uploadSpeed = parseSpeed(speed * 100)
          }
          this.uploadPreLoaded = this.uploadLoaded
        }, 100)

        const res = await doUpload(data, {
          onUploadProgress: (progressEvent) => {
            this.uploadLoaded = progressEvent.loaded
            const complete
              = ((progressEvent.loaded / progressEvent.total) * 100) | 0
            this.uploadComplete = complete
            if (complete === 100) {
              clearInterval(this.speedTimer)
            }
          },
        }, this.platform)

        if (res.data) {
          this.$emit('update', res.data)
          this.uploadStaticStatus = 'succ'
        }
      } catch (err) {
        this.uploadStaticStatus = 'wait'
        if (err.resp_common) {
          this.$emit('error', err.resp_common.msg)
          return
        }

        this.$emit('error', err.message)
      }

      this.$forceUpdate()
      clearInterval(this.speedTimer)
    },
  },
}
</script>

<style lang="scss" scoped>
.static-wallpaper-uploader {
  margin: 0 auto;
  user-select: none;
}
.upload-static {
  position: relative;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  border: 2px dashed #ccc;
  .icon-upload {
    display: block;
    width: 163px;
    height: 107px;
    margin: 82px auto 32px;
    background: url('~@/assets/images/icon-upload-pic.png') 0 0 no-repeat;
  }
  .upload-info {
    margin-bottom: 8px;
    font-weight: 700;
    font-size: 16px;
    color: #333;
  }
  .upload-sub {
    font-size: 12px;
    color: #333;
    .btn {
      display: inline-block;
      width: 45px;
      height: 20px;
      margin: 0 5px;
      line-height: 20px;
      text-align: center;
      font-weight: 700;
      background: #f5b903;
      border-radius: 2px;
      color: #333;
    }
  }
  .upload-tip {
    margin-top: 80px;
    font-size: 12px;
    font-weight: 700;
    color: #a5a5a5;
    &.for-mobile {
      margin-top: 60px;
    }
  }
}

.icon-pc {
  display: block;
  width: 130px;
  height: 102px;
  margin: 82px auto 32px;
  background: url('~@/assets/images/upload/icon-pc.png') 0 0 no-repeat;
}
.icon-mobile {
  display: block;
  width: 61px;
  height: 102px;
  margin: 82px auto 32px;
  background: url('~@/assets/images/upload/icon-mobile.png') 0 0 no-repeat;
}

.upload-static-process {
  position: relative;
  width: 100%;
  height: 100%;
  border: 2px dashed #ccc;
  .upload-process-text {
    position: absolute;
    left: 0;
    right: 0;
    top: 60%;
    text-align: center;
    font-size: 16px;
    color: #333;
  }
  .upload-speed {
    position: absolute;
    left: 0;
    right: 0;
    top: 67%;
    text-align: center;
    font-size: 12px;
    color: #a5a5a5;
  }
}

.upload-static-succ {
  position: relative;
  width: 100%;
  height: 100%;
  .pic {
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }
  .btn-remove {
    z-index: 2;
    position: absolute;
    right: 6px;
    top: 6px;
    width: 36px;
    height: 36px;
    line-height: 36px;
    text-align: center;
    font-size: 14px;
    background: #333;
    border-radius: 4px;
    color: #999;
    cursor: pointer;
  }
}

.loading-progress {
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -53px;
  margin-top: -73px;
}
</style>
